import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieSettingsPage() {
  const { t, i18n } = useTranslation('common');
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      try {
        setPreferences(JSON.parse(consent));
      } catch (e) {
        // Keep defaults
      }
    }
  }, []);

  const savePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const getTitle = () => {
    if (i18n.language === 'de') return 'Cookie-Einstellungen';
    if (i18n.language === 'es') return 'Configuración de Cookies';
    return 'Cookie Settings';
  };

  const getDescription = () => {
    if (i18n.language === 'de') return 'Verwalten Sie Ihre Cookie-Präferenzen. Sie können Ihre Einstellungen jederzeit ändern.';
    if (i18n.language === 'es') return 'Gestione sus preferencias de cookies. Puede cambiar su configuración en cualquier momento.';
    return 'Manage your cookie preferences. You can change your settings at any time.';
  };

  const getSaveButton = () => {
    if (i18n.language === 'de') return 'Einstellungen speichern';
    if (i18n.language === 'es') return 'Guardar Preferencias';
    return 'Save Preferences';
  };

  const getCategories = () => {
    if (i18n.language === 'es') {
      return {
        necessary: {
          title: 'Cookies Necesarias',
          description: 'Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.'
        },
        functional: {
          title: 'Cookies Funcionales',
          description: 'Estas cookies permiten funcionalidades mejoradas y personalización.'
        },
        analytics: {
          title: 'Cookies de Análisis',
          description: 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.'
        },
        marketing: {
          title: 'Cookies de Marketing',
          description: 'Estas cookies se utilizan para rastrear visitantes en sitios web para mostrar anuncios relevantes.'
        }
      };
    }

    if (i18n.language === 'de') {
      return {
        necessary: {
          title: 'Notwendige Cookies',
          description: 'Diese Cookies sind für die Funktion der Website unerlässlich und können nicht deaktiviert werden.'
        },
        functional: {
          title: 'Funktionale Cookies',
          description: 'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung.'
        },
        analytics: {
          title: 'Analyse-Cookies',
          description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.'
        },
        marketing: {
          title: 'Marketing-Cookies',
          description: 'Diese Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen, um relevante Anzeigen anzuzeigen.'
        }
      };
    }

    return {
      necessary: {
        title: 'Necessary Cookies',
        description: 'These cookies are essential for the website to function and cannot be disabled.'
      },
      functional: {
        title: 'Functional Cookies',
        description: 'These cookies enable enhanced functionality and personalization.'
      },
      analytics: {
        title: 'Analytics Cookies',
        description: 'These cookies help us understand how visitors interact with our website.'
      },
      marketing: {
        title: 'Marketing Cookies',
        description: 'These cookies are used to track visitors across websites to display relevant advertisements.'
      }
    };
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-24">
        <div className="max-w-[800px] mx-auto px-10">
          <h1
            className="text-5xl font-bold text-[#0A2D50] mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('cookies.settingsTitle')}
          </h1>
          <p className="text-base text-gray-700 leading-relaxed mb-12">
            {t('cookies.settingsIntro')}
          </p>

          <div className="space-y-6 mb-8">
            <div className="border-2 border-[#F2F4F5] rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#0A2D50] mb-2">
                    {t('cookies.necessary')}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('cookies.necessaryDesc')}
                  </p>
                </div>
                <div className="ml-4 text-sm font-medium text-gray-500 whitespace-nowrap">
                  {i18n.language === 'de' ? 'Immer aktiv' : 'Always active'}
                </div>
              </div>
            </div>

            <div className="border-2 border-[#F2F4F5] rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#0A2D50] mb-2">
                    {t('cookies.functional')}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('cookies.functionalDesc')}
                  </p>
                </div>
                <label className="ml-4 relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>
            </div>

            <div className="border-2 border-[#F2F4F5] rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#0A2D50] mb-2">
                    {t('cookies.analytics')}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('cookies.analyticsDesc')}
                  </p>
                </div>
                <label className="ml-4 relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>
            </div>

            <div className="border-2 border-[#F2F4F5] rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#0A2D50] mb-2">
                    {t('cookies.marketing')}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('cookies.marketingDesc')}
                  </p>
                </div>
                <label className="ml-4 relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#1BCDFF]"></div>
                </label>
              </div>
            </div>
          </div>

          <button
            onClick={savePreferences}
            className="bg-[#1BCDFF] text-white px-8 py-3 rounded-lg text-sm font-medium hover:bg-[#1EB7EC] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {i18n.language === 'de' ? 'Einstellungen speichern' : 'Save Settings'}
          </button>

          {saved && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                {i18n.language === 'de' ? 'Ihre Einstellungen wurden gespeichert.' : 'Your settings have been saved.'}
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}